// Section 6 — structured RACI matrix. Activities grouped into numbered
// subsections (6.1 Design / 6.2 Construction / 6.3 Commissioning) and category,
// with one clickable R/A/C/I mark cell per party. Parties are DYNAMIC — any
// number per turnover, renamable inline, addable/removable (the party flagged
// IsCleanSpace anchors the scope logic and cannot be removed). Rows where the
// CleanSpace party is R or A are the CleanSpace scope — the footer bar pushes
// those to the turnover's estimator as an action item.
// Same inline-edit pattern as LongLeadItemsPanel / ScheduleMilestonesPanel.

export const RACI_PHASES = [
  { key: "Design",        num: "6.1" },
  { key: "Construction",  num: "6.2" },
  { key: "Commissioning", num: "6.3" },
];

// Rows seeded before the 6.3 rename carry the old phase string.
const phaseMatches = (item, key) =>
  item.Phase === key || (key === "Commissioning" && item.Phase === "Startup & Commissioning");

const MARK_STYLE = {
  R: { background: "#1e3a5f", color: "#fff",    border: "1px solid #1e3a5f" },
  A: { background: "#fef3c7", color: "#92400e", border: "1px solid #fcd34d" },
  C: { background: "#ccfbf1", color: "#0f766e", border: "1px solid #5eead4" },
  I: { background: "#f3f4f6", color: "#6b7280", border: "1px solid #e5e7eb" },
};

const MARK_LEGEND = [
  { mark: "R", label: "Responsible", desc: "Does the actual work for the task" },
  { mark: "A", label: "Accountable", desc: "Owns success of the task; decision-maker" },
  { mark: "C", label: "Consulted",   desc: "Consulted for details and requirements" },
  { mark: "I", label: "Informed",    desc: "Kept informed of major updates" },
];

export function getMark(assignments, itemId, partyId) {
  return assignments?.[itemId]?.[partyId] || null;
}

// CleanSpace scope = the IsCleanSpace party holds R or A on the row.
export function isCsScope(item, parties, assignments) {
  const cs = parties.find(p => p.IsCleanSpace);
  if (!cs) return false;
  const mark = getMark(assignments, item.RaciItemID, cs.RaciPartyID);
  return mark === "R" || mark === "A";
}

export default function RaciMatrixPanel({
  items,
  parties,
  assignments,
  savingId,
  onCycleMark,
  onSaveItem,
  onDeleteItem,
  newItem,
  onNewItemChange,
  onAdd,
  adding,
  onSeed,
  seeding,
  onRenameParty,
  onAddParty,
  onDeleteParty,
  addingParty,
  onSendScope,
  sendingScope,
  estimatorName,
}) {
  const cellInput = {
    width: "100%", padding: "4px 8px", fontSize: 12,
    border: "1px solid var(--cs-border)", borderRadius: 5,
    background: "var(--surface)", color: "var(--text)",
  };

  const scopeItems = items.filter(i => isCsScope(i, parties, assignments));
  const unsentScope = scopeItems.filter(i => !i.SentToEstimating);

  // ── Empty state: offer the standard template ────────────────────────────────
  if (items.length === 0) {
    return (
      <div style={{ marginBottom: 24 }}>
        <div style={{ padding: "18px 20px", background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#0369a1", marginBottom: 4 }}>
            🗂 Responsibility (RACI) Matrix
          </div>
          <div style={{ fontSize: 12, color: "#1e3a5f", marginBottom: 12 }}>
            Track who is Responsible, Accountable, Consulted, and Informed for every project
            activity — organized into 6.1&nbsp;Design, 6.2&nbsp;Construction, and
            6.3&nbsp;Commissioning. Load the standard CleanSpace template to start, rename the
            party columns to this project&rsquo;s actual firms and people, and adjust marks to
            match the project&rsquo;s responsibility matrix. Rows where CleanSpace is R or A
            become the scope list you send to the estimator.
          </div>
          <button
            onClick={onSeed}
            disabled={seeding}
            style={{
              padding: "8px 18px", fontSize: 13, fontWeight: 700,
              background: "var(--cs-blue)", color: "#fff",
              border: "none", borderRadius: 6, cursor: "pointer",
              opacity: seeding ? 0.6 : 1,
            }}
          >
            {seeding ? "Loading template…" : "Load Standard RACI Template"}
          </button>
        </div>
      </div>
    );
  }

  // Group items into numbered subsections; unknown phases tail without a number
  const phaseDefs = [
    ...RACI_PHASES.filter(p => items.some(i => phaseMatches(i, p.key))),
    ...[...new Set(items.map(i => i.Phase))]
      .filter(ph => !RACI_PHASES.some(p => phaseMatches({ Phase: ph }, p.key)))
      .map(ph => ({ key: ph, num: null })),
  ];
  const anchorId = (key) => `raci-sub-${key.replace(/\W+/g, "-").toLowerCase()}`;

  const gridCols = `minmax(220px, 1fr) repeat(${parties.length}, 64px) minmax(140px, 220px) 36px`;
  const gridMinWidth = 460 + parties.length * 64;

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, flexWrap: "wrap", gap: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text2)" }}>
          Responsibility (RACI) Matrix
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {MARK_LEGEND.map(({ mark, label, desc }) => (
            <span key={mark} title={desc} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, color: "var(--text3)" }}>
              <span style={{ ...MARK_STYLE[mark], borderRadius: 4, fontSize: 10, fontWeight: 800, padding: "1px 6px" }}>{mark}</span>
              {label}
            </span>
          ))}
        </div>
      </div>

      {/* Subsection quick-jump + party management */}
      <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap", alignItems: "center" }}>
        {phaseDefs.map(p => (
          <button
            key={p.key}
            onClick={() => document.getElementById(anchorId(p.key))?.scrollIntoView({ behavior: "smooth", block: "start" })}
            style={{
              padding: "4px 12px", fontSize: 12, fontWeight: 700,
              background: "var(--surface)", color: "var(--cs-blue)",
              border: "1px solid var(--cs-border)", borderRadius: 999, cursor: "pointer",
            }}
          >
            {p.num ? `${p.num} ${p.key}` : p.key}
          </button>
        ))}
        <span style={{ flex: 1 }} />
        <button
          onClick={onAddParty}
          disabled={addingParty}
          title="Add a party column (a firm or person responsible for work on this project)"
          style={{
            padding: "4px 12px", fontSize: 12, fontWeight: 700,
            background: "var(--surface)", color: "var(--text2)",
            border: "1px dashed var(--cs-border)", borderRadius: 999, cursor: "pointer",
            opacity: addingParty ? 0.5 : 1,
          }}
        >
          {addingParty ? "Adding…" : "+ Add Party"}
        </button>
      </div>

      <div style={{ border: "1px solid var(--cs-border)", borderRadius: 8, overflowX: "auto" }}>
        <div style={{ minWidth: gridMinWidth }}>
          {/* Header — party names are editable inline (name the actual firm/person) */}
          <div style={{ display: "grid", gridTemplateColumns: gridCols, background: "#f8f9fb", borderBottom: "1px solid var(--cs-border)" }}>
            <div style={{ padding: "8px 14px", fontSize: 11, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Activity / Deliverable
            </div>
            {parties.map(p => (
              <div key={p.RaciPartyID} style={{ padding: "4px 2px", position: "relative" }}>
                <input
                  defaultValue={p.PartyName}
                  onBlur={e => {
                    const v = e.target.value.trim();
                    if (v && v !== p.PartyName) onRenameParty(p, v);
                    else e.target.value = p.PartyName;
                  }}
                  title={`${p.PartyName}${p.IsCleanSpace ? " (CleanSpace — anchors scope tracking)" : ""} — click to rename`}
                  style={{
                    width: "100%", border: "none", background: "transparent",
                    fontSize: 10, fontWeight: 700,
                    color: p.IsCleanSpace ? "var(--cs-blue)" : "var(--text3)",
                    textAlign: "center", textTransform: "uppercase", letterSpacing: "0.03em",
                    outline: "none", fontFamily: "inherit",
                  }}
                />
                {!p.IsCleanSpace && (
                  <button
                    onClick={() => onDeleteParty(p)}
                    title={`Remove ${p.PartyName} and its marks`}
                    style={{
                      position: "absolute", top: -2, right: 0,
                      background: "none", border: "none", cursor: "pointer",
                      fontSize: 10, color: "#d1d5db", padding: 2, lineHeight: 1,
                    }}
                    onMouseEnter={e => e.currentTarget.style.color = "#dc2626"}
                    onMouseLeave={e => e.currentTarget.style.color = "#d1d5db"}
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
            <div style={{ padding: "8px 14px", fontSize: 11, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Notes
            </div>
            <div />
          </div>

          {phaseDefs.map(p => {
            const phaseItems = items.filter(i => phaseMatches(i, p.key));
            let lastCategory = null;
            return (
              <div key={p.key} id={anchorId(p.key)} style={{ scrollMarginTop: 12 }}>
                <div style={{ padding: "7px 14px", background: "#1e3a5f", fontSize: 11, fontWeight: 800, color: "#fff", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                  {p.num ? `${p.num} — ${p.key}` : p.key}
                </div>
                {phaseItems.map((item, i) => {
                  const showCategory = item.Category && item.Category !== lastCategory;
                  lastCategory = item.Category || lastCategory;
                  const scope = isCsScope(item, parties, assignments);
                  return (
                    <div key={item.RaciItemID}>
                      {showCategory && (
                        <div style={{ padding: "5px 14px", background: "#eef2f7", fontSize: 10.5, fontWeight: 700, color: "var(--text2)", textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "1px solid var(--cs-border)" }}>
                          {item.Category}
                        </div>
                      )}
                      <div style={{
                        display: "grid", gridTemplateColumns: gridCols, alignItems: "center",
                        borderBottom: i < phaseItems.length - 1 ? "1px solid var(--cs-border)" : "none",
                        background: scope ? "#eff6ff" : i % 2 === 0 ? "transparent" : "#fafbfc",
                        borderLeft: scope ? "3px solid var(--cs-blue)" : "3px solid transparent",
                        opacity: savingId === item.RaciItemID ? 0.55 : 1,
                      }}>
                        <div style={{ padding: "7px 14px", display: "flex", alignItems: "center", gap: 6 }}>
                          {item.IsCustom ? (
                            <input
                              defaultValue={item.Activity}
                              onBlur={e => { if (e.target.value.trim() && e.target.value !== item.Activity) onSaveItem({ ...item, Activity: e.target.value }); }}
                              style={{ flex: 1, border: "none", background: "transparent", fontSize: 12.5, color: "var(--text)", fontFamily: "inherit", outline: "none", fontStyle: "italic" }}
                            />
                          ) : (
                            <span style={{ fontSize: 12.5, color: "var(--text)" }}>{item.Activity}</span>
                          )}
                          {scope && item.SentToEstimating ? (
                            <span title="Included in a scope push to the estimator" style={{ fontSize: 10, fontWeight: 700, color: "#15803d", whiteSpace: "nowrap" }}>✓ sent</span>
                          ) : null}
                        </div>
                        {parties.map(pt => {
                          const mark = getMark(assignments, item.RaciItemID, pt.RaciPartyID);
                          return (
                            <div key={pt.RaciPartyID} style={{ padding: "5px 4px", textAlign: "center" }}>
                              <button
                                onClick={() => onCycleMark(item, pt)}
                                title={`${pt.PartyName} — click to cycle blank → R → A → C → I`}
                                style={{
                                  width: 34, height: 26, borderRadius: 5, cursor: "pointer",
                                  fontSize: 12, fontWeight: 800, fontFamily: "inherit",
                                  ...(mark ? MARK_STYLE[mark] : { background: "var(--surface)", color: "#d1d5db", border: "1px dashed var(--cs-border)" }),
                                }}
                              >
                                {mark || "·"}
                              </button>
                            </div>
                          );
                        })}
                        <div style={{ padding: "5px 10px" }}>
                          <input
                            defaultValue={item.Notes || ""}
                            onBlur={e => { if ((e.target.value || null) !== (item.Notes || null)) onSaveItem({ ...item, Notes: e.target.value || null }); }}
                            placeholder="—"
                            title={item.Notes || ""}
                            style={cellInput}
                          />
                        </div>
                        <div style={{ padding: "5px 6px", textAlign: "center" }}>
                          <button
                            onClick={() => onDeleteItem(item)}
                            title="Remove this activity from the matrix"
                            style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, color: "#d1d5db", padding: "2px 4px" }}
                            onMouseEnter={e => e.currentTarget.style.color = "#dc2626"}
                            onMouseLeave={e => e.currentTarget.style.color = "#d1d5db"}
                          >
                            🗑
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

      {/* Add custom activity */}
      <div style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "center", flexWrap: "wrap" }}>
        <select
          value={newItem.phase}
          onChange={e => onNewItemChange({ ...newItem, phase: e.target.value })}
          style={{ padding: "6px 8px", fontSize: 13, border: "1px solid var(--cs-border)", borderRadius: 6, background: "var(--surface)", color: "var(--text)" }}
        >
          {RACI_PHASES.map(p => <option key={p.key} value={p.key}>{p.num} — {p.key}</option>)}
        </select>
        <input
          value={newItem.category}
          onChange={e => onNewItemChange({ ...newItem, category: e.target.value })}
          placeholder="Category (optional)"
          style={{ width: 170, padding: "6px 10px", fontSize: 13, border: "1px solid var(--cs-border)", borderRadius: 6, background: "var(--surface)", color: "var(--text)" }}
        />
        <input
          value={newItem.activity}
          onChange={e => onNewItemChange({ ...newItem, activity: e.target.value })}
          onKeyDown={e => e.key === "Enter" && onAdd()}
          placeholder="Add activity / deliverable…"
          style={{ flex: "1 1 220px", padding: "6px 10px", fontSize: 13, border: "1px solid var(--cs-border)", borderRadius: 6, background: "var(--surface)", color: "var(--text)" }}
        />
        <button
          onClick={onAdd}
          disabled={adding || !newItem.activity.trim()}
          style={{
            padding: "6px 16px", fontSize: 13, fontWeight: 700,
            background: "var(--cs-blue)", color: "#fff",
            border: "none", borderRadius: 6, cursor: "pointer",
            opacity: (adding || !newItem.activity.trim()) ? 0.5 : 1,
            whiteSpace: "nowrap",
          }}
        >
          {adding ? "Adding…" : "+ Add"}
        </button>
      </div>

      {/* CleanSpace scope → estimator action item */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap",
        marginTop: 14, padding: "12px 16px",
        background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 8,
      }}>
        <div style={{ fontSize: 12.5, color: "#1e3a5f" }}>
          <strong>{scopeItems.length}</strong> CleanSpace scope {scopeItems.length === 1 ? "item" : "items"} (marked R or A)
          {scopeItems.length > 0 && (
            <span style={{ color: "#3b82f6" }}>
              {" "}— {unsentScope.length === 0 ? "all sent to estimating" : `${unsentScope.length} not yet sent to estimating`}
            </span>
          )}
        </div>
        {onSendScope && <button
          onClick={onSendScope}
          disabled={sendingScope || scopeItems.length === 0}
          title={estimatorName
            ? `Creates an action item for ${estimatorName} listing the CleanSpace-responsible activities`
            : "Creates a scope-review action item; assign an estimator on the cover sheet to route it automatically"}
          style={{
            padding: "7px 16px", fontSize: 13, fontWeight: 700,
            background: "#1e3a5f", color: "#fff",
            border: "none", borderRadius: 6, cursor: "pointer",
            opacity: (sendingScope || scopeItems.length === 0) ? 0.5 : 1,
            whiteSpace: "nowrap",
          }}
        >
          {sendingScope ? "Sending…" : `Send Scope to ${estimatorName ? estimatorName.split(" ")[0] : "Estimator"} →`}
        </button>}
      </div>
    </div>
  );
}
